import { useParams } from 'react-router-dom';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { buildQueryParams } from 'utils/queryParamsUtil';

import {
  CreatePlan,
  GetPlanResponse,
  Plan,
  PlanStatus,
  UpdatePlan
} from '../common/contracts';
import { EQueryKeys } from '../constants/enums';
import useAxios from '../hooks/useAxios';

import { EApiRoutes, fetchConfig, getApiRouteOrMock } from './api.utils';

interface PlansFilters {
  pageSize: number;
  offset: number;
  publisherId?: string;
  startDate?: string;
  endDate?: string;
  billingPeriodUnit?: string;
}

export const DEFAULT_PLANS_FILTERS: PlansFilters = {
  pageSize: 10,
  offset: 0
};

export default function usePlans(
  plansFilters: PlansFilters = DEFAULT_PLANS_FILTERS,
  planId?: string
) {
  const axios = useAxios();
  const queryClient = useQueryClient();
  const { publisherId } = useParams();

  const getPlans = useQuery<GetPlanResponse>({
    ...fetchConfig.general,
    queryKey: [
      EQueryKeys.SUBSCRIPTION_PLANS,
      publisherId,
      JSON.stringify(plansFilters)
    ],
    queryFn: async () => {
      const plansPath = `${getApiRouteOrMock(EApiRoutes.PLANS)}?${buildQueryParams(plansFilters)}&getCoupons=true`;

      return await axios.get(
        plansPath,
        {},
        publisherId ? { 'x-publishers': publisherId } : undefined
      );
    }
  });

  const getPlan = useQuery<Plan>({
    ...fetchConfig.general,
    queryKey: [EQueryKeys.SUBSCRIPTION_PLAN, publisherId, planId],
    enabled: !!planId,
    queryFn: async () => {
      const planPath = `${getApiRouteOrMock(EApiRoutes.PLANS)}/plan/${planId}?getCoupon=true`;

      return await axios.get(
        planPath,
        {},
        publisherId ? { 'x-publishers': publisherId } : undefined
      );
    }
  });

  const createPlan = useMutation<Plan, Error, CreatePlan>({
    mutationFn: async (planData: CreatePlan): Promise<Plan> => {
      const plansPath = `${getApiRouteOrMock(EApiRoutes.PLANS)}`;

      const response = await axios.post(
        plansPath,
        planData,
        publisherId ? { 'x-publishers': publisherId } : undefined
      );

      await queryClient.invalidateQueries({
        queryKey: [EQueryKeys.SUBSCRIPTION_PLANS, publisherId]
      });

      return response.data;
    }
  });

  const updatePlan = useMutation<
    UpdatePlan,
    Error,
    { planId: string; planData: UpdatePlan }
  >({
    mutationFn: async ({ planId, planData }): Promise<Plan> => {
      const plansPath = `${getApiRouteOrMock(EApiRoutes.PLANS)}/plan/${planId}`;
      const response = await axios.patch(plansPath, planData);

      await queryClient.invalidateQueries({
        queryKey: [EQueryKeys.SUBSCRIPTION_PLANS, publisherId]
      });

      return response.data;
    }
  });

  const archivePlan = useMutation<Plan, Error, string>({
    mutationFn: async (planId: string): Promise<Plan> => {
      const plansPath = `${getApiRouteOrMock(EApiRoutes.PLANS)}/plan/${planId}/archive`;

      const response = await axios.patch(plansPath, {
        status: PlanStatus.ARCHIVED
      });

      await queryClient.invalidateQueries({
        queryKey: [EQueryKeys.SUBSCRIPTION_PLANS, publisherId]
      });

      return response.data;
    }
  });

  return {
    getPlans,
    getPlan,
    createPlan,
    updatePlan,
    archivePlan
  };
}
